/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eul      model.update();
      model.draw(projection); // 参照渡しなのprojectionは変質する。

      ite.preIncrement();
    }
  }ve2d-open-software-license-agreement_en.html.
 */

import { CubismMatrix44 } from '@framework/math/cubismmatrix44';
import { ACubismMotion } from '@framework/motion/acubismmotion';
import { csmVector } from '@framework/type/csmvector';
import { csmMap } from '@framework/type/csmmap';

import * as LAppDefine from './lappdefine';
import { canvas } from './lappglmanager';
import { LAppModel, LoadStep } from './lappmodel';
import { LAppPal } from './lapppal';

export let s_instance: LAppLive2DManager = null;

/**
 * Describes a sub-model entry inside a pack.json file.
 * Models that ship with a pack.json at their root are treated as
 * multi-model packs (e.g. separate head + body moc3 files).
 */
export interface PackSubModel {
  id: string;        // e.g. "body", "head"
  path: string;      // relative folder, e.g. "body/"
  model3: string;    // model3.json filename inside that folder
  drawOrder?: number; // lower = drawn first (behind). Used for non-split models.
  /** Render-order position at which to split this model into back/front passes. */
  splitAt?: number;
  /** Draw order for the back pass (render orders < splitAt). */
  backDrawOrder?: number;
  /** Draw order for the front pass (render orders >= splitAt). */
  frontDrawOrder?: number;
  /** Update every N frames (1=every frame, 3=every 3rd frame). Reduces CPU for static models. */
  updateEveryNFrames?: number;
  /** Physics evaluate every N model updates (1=every update, 4=quarter rate). */
  physicsEveryNFrames?: number;
  /** Hard-disable physics for this sub-model. Useful for static clothing/body meshes. */
  disablePhysics?: boolean;
}

type LightState = {
  color: { r: number; g: number; b: number; a: number };
  intensity: number;
  transitionMs: number;
};

const DEFAULT_LIGHT_TRANSITION_MS = 400;

/**
 * サンプルアプリケーションにおいてCubismModelを管理するクラス
 * モデル生成と破棄、タップイベントの処理、モデル切り替えを行う。
 */
export class LAppLive2DManager {
  /**
   * クラスのインスタンス（シングルトン）を返す。
   * インスタンスが生成されていない場合は内部でインスタンスを生成する。
   *
   * @return クラスのインスタンス
   */
  public static getInstance(): LAppLive2DManager {
    if (s_instance == null) {
      s_instance = new LAppLive2DManager();
    }

    return s_instance;
  }

  /**
   * クラスのインスタンス（シングルトン）を解放する。
   */
  public static releaseInstance(): void {
    if (s_instance != null) {
      s_instance = void 0;
    }

    s_instance = null;
  }

  /**
   * 現在のシーンで保持しているモデルを返す。
   *
   * @param no モデルリストのインデックス値
   * @return モデルのインスタンスを返す。インデックス値が範囲外の場合はNULLを返す。
   * @deprecated Use getModelById() instead for more reliable model access
   */
  public getModel(no: number): LAppModel {
    // For backwards compatibility, return current model if index is 0
    if (no === 0 && this._currentModelId) {
      // For multi-model packs, return the first sub-model
      if (this._currentSubModelKeys.length > 0) {
        return this.getModelById(this._currentSubModelKeys[0]);
      }
      return this.getModelById(this._currentModelId);
    }
    return null;
  }

  /**
   * モデルIDからモデルを取得
   * @param modelId モデルのID
   * @return モデルのインスタンス。存在しない場合はnull
   */
  public getModelById(modelId: string): LAppModel {
    const it = this._models.getValue(modelId);
    return it !== null ? it : null;
  }

  /**
   * Returns all active sub-models for the current pack.
   * For single-model packs (no pack.json), returns an array with just that model.
   * For multi-model packs, returns all sub-models in draw order.
   */
  public getActiveModels(): LAppModel[] {
    if (this._currentSubModelKeys.length > 0) {
      const models: LAppModel[] = [];
      for (const key of this._currentSubModelKeys) {
        const m = this._models.getValue(key);
        if (m) models.push(m);
      }
      return models;
    }
    // Legacy single-model fallback
    if (this._currentModelId) {
      const m = this._models.getValue(this._currentModelId);
      return m ? [m] : [];
    }
    return [];
  }

  /**
   * 現在表示中のモデルIDを取得
   * @return 現在のモデルID。モデルがない場合はnull
   */
  public getCurrentModelId(): string {
    return this._currentModelId;
  }

  /**
   * Apply a Y offset to the currently active model's matrix.
   * @param offset Offset value in model coordinates.
   */
  public setModelYOffset(offset: number, durationMs = 0): void {
    this._pendingModelYOffset = offset;
    this._pendingModelYOffsetDuration = durationMs;
    if (!this._currentModelId) {
      return;
    }

    for (const model of this.getActiveModels()) {
      if (typeof model.setModelYOffset === 'function') {
        model.setModelYOffset(offset, durationMs);
      }
    }
  }

  public setModelXOffset(offset: number, durationMs = 0): void {
    this._pendingModelXOffset = offset;
    this._pendingModelXOffsetDuration = durationMs;
    if (!this._currentModelId) {
      return;
    }

    for (const model of this.getActiveModels()) {
      if (typeof model.setModelXOffset === 'function') {
        model.setModelXOffset(offset, durationMs);
      }
    }
  }

  /**
   * Update the ambient light tint affecting the active model.
   * @param colorInput Hex string, RGB(A) string, numeric value, or object describing the target color.
   * @param intensity Blend weight between 0.0 (no effect) and 1.0 (full tint).
   * @returns true if the request was accepted, false when the color could not be parsed.
   */
  public setModelLightColor(
    colorInput: string | number | { r: number; g: number; b: number; a?: number },
    intensity: number | string = 1,
    transitionMs: number | string = DEFAULT_LIGHT_TRANSITION_MS
  ): boolean {
    const parsedColor = this._parseColorInput(colorInput);
    if (!parsedColor) {
      console.error('[Live2D] Failed to parse light color input:', colorInput);
      return false;
    }

    const numericIntensity =
      typeof intensity === 'string' ? parseFloat(intensity) : intensity;
    const normalizedIntensity = this._normalizeLightIntensity(numericIntensity);
    const numericTransition =
      typeof transitionMs === 'string' ? parseFloat(transitionMs) : transitionMs;
    const safeTransition = Number.isFinite(numericTransition) && numericTransition >= 0
      ? numericTransition
      : DEFAULT_LIGHT_TRANSITION_MS;
    this._pendingLightState = {
      color: { ...parsedColor },
      intensity: normalizedIntensity,
      transitionMs: safeTransition
    };

    for (const model of this.getActiveModels()) {
      this._applyPendingLightToModel(model);
    }

    return true;
  }

  /**
   * Clear the ambient light tint, returning the model to its neutral color.
   */
  public clearModelLightColor(transitionMs: number | string = DEFAULT_LIGHT_TRANSITION_MS): void {
    const numericTransition =
      typeof transitionMs === 'string' ? parseFloat(transitionMs) : transitionMs;
    const safeTransition = Number.isFinite(numericTransition) && numericTransition >= 0
      ? numericTransition
      : DEFAULT_LIGHT_TRANSITION_MS;

    const base = this._defaultLightState();
    base.transitionMs = safeTransition;
    this._pendingLightState = base;

    for (const model of this.getActiveModels()) {
      model.resetLightColor(safeTransition);
    }
  }

  /**
   * Retrieve the currently configured ambient light tint.
   */
  public getCurrentModelLightColor(): LightState {
    return this._cloneLightState(
      this._pendingLightState ?? this._defaultLightState()
    );
  }

  /**
   * 現在のシーンで保持しているすべてのモデルを解放する
   */
  public releaseAllModel(): void {
    const ite = this._models.begin();
    while (ite.notEqual(this._models.end())) {
      const model = ite.ptr().second;
      if (model) {
        model.release();
      }
      ite.preIncrement();
    }
    this._models.clear();
    this._currentModelId = null;
    this._currentSubModelKeys = [];
  }

  /**
   * 画面をドラッグした時の処理
   *
   * @param x 画面のX座標
   * @param y 画面のY座標
   */
  public onDrag(x: number, y: number): void {
    const ite = this._models.begin();
    while (ite.notEqual(this._models.end())) {
      const model = ite.ptr().second;
      if (model) {
        model.setDragging(x, y);
      }
      ite.preIncrement();
    }
  }

  /**
   * 画面をタップした時の処理
   *
   * @param x 画面のX座標
   * @param y 画面のY座標
   */
  public onTap(x: number, y: number): void {
    if (LAppDefine.DebugLogEnable) {
      LAppPal.printMessage(
        `[APP]tap point: {x: ${x.toFixed(2)} y: ${y.toFixed(2)}}`
      );
    }

    // Hit-test all active sub-models in reverse draw order (top layer first)
    const models = this.getActiveModels();
    for (let i = models.length - 1; i >= 0; i--) {
      const model = models[i];
      if (model) {
        const hitAreaNames = model.getHitAreaNames();
        for (const hitAreaName of hitAreaNames) {
          if (model.hitTest(hitAreaName, x, y)) {
            if (LAppDefine.DebugLogEnable) {
              LAppPal.printMessage(
                `[APP]hit area: [${hitAreaName}]`
              );
            }
            const motionGroup = `Tap${hitAreaName}`;
            // Play tap motion on ALL sub-models so they react together
            for (const m of models) {
              m.startRandomMotion(
                motionGroup,
                LAppDefine.PriorityNormal,
                this._finishedMotion
              );
            }
            return; // Only handle the first hit
          }
        }
      }
    }
  }

  /**
   * 画面を更新するときの処理
   * モデルの更新処理及び描画処理を行う。
   * Multi-pass rendering: split models are drawn in two passes (back + front).
   */
  private _debugUpdateLogTimer = 0;
  /** Last frame profiling data (populated every frame, read by overlay). */
  public lastFrameProfile: {
    totalMs: number;
    updateMs: number;
    drawMs: number;
    modelCount: number;
    passCount: number;
    drawableCount: number;
  } = { totalMs: 0, updateMs: 0, drawMs: 0, modelCount: 0, passCount: 0, drawableCount: 0 };

  public onUpdate(): void {
    const frameStart = performance.now();
    const { width, height } = canvas;

    // Log model states every ~3 seconds for debugging multi-model
    this._debugUpdateLogTimer++;
    if (this._debugUpdateLogTimer % 180 === 1) {
      const keys: string[] = [];
      const states: string[] = [];
      let it2 = this._models.begin();
      while (it2.notEqual(this._models.end())) {
        keys.push(it2.ptr().first);
        const m = it2.ptr().second;
        states.push(m ? `state=${m.getLoadState()}, hasModel=${!!m.getModel()}` : 'null');
        it2.preIncrement();
      }
      console.log(`[onUpdate] Models in map: ${keys.length}, keys=[${keys.join(', ')}], states=[${states.join(' | ')}], passes=${this._renderPasses.length}`);
    }

    // --- Phase 1: Update all models (physics, blink, drag) — once per model ---
    const updateStart = performance.now();
    let totalDrawables = 0;
    const updatedModels = new Set<string>();
    const ite = this._models.begin();
    while (ite.notEqual(this._models.end())) {
      const model: LAppModel = ite.ptr().second;
      if (model) {
        model.update();
        updatedModels.add(ite.ptr().first);
        if (model.getModel()) {
          totalDrawables += model.getModel().getDrawableCount();
        }
      }
      ite.preIncrement();
    }
    const updateEnd = performance.now();

    // --- Phase 2: Draw using render passes (multi-pass for split models) ---
    if (this._renderPasses.length > 0) {
      // Multi-model pack with explicit render passes
      for (const pass of this._renderPasses) {
        const model = this._models.getValue(pass.subKey);
        if (!model || !model.getModel()) continue;

        const projection: CubismMatrix44 = new CubismMatrix44();
        if (model.getModel().getCanvasWidth() > 1.0 && width < height) {
          model.getModelMatrix().setWidth(2.0);
          projection.scale(1.0, width / height);
        } else {
          projection.scale(height / width, 1.0);
        }
        if (this._viewMatrix != null) {
          projection.multiplyByMatrix(this._viewMatrix);
        }

        if (pass.minOrder >= 0 || pass.maxOrder >= 0) {
          // Filtered draw — only a range of drawables
          model.drawWithOrderRange(projection, pass.minOrder, pass.maxOrder);
        } else {
          // Full draw — all drawables
          model.draw(projection);
        }
      }
    } else {
      // Legacy single-model path (no pack.json / no render passes configured)
      const ite2 = this._models.begin();
      while (ite2.notEqual(this._models.end())) {
        const model: LAppModel = ite2.ptr().second;
        if (model && model.getModel()) {
          const projection: CubismMatrix44 = new CubismMatrix44();
          if (model.getModel().getCanvasWidth() > 1.0 && width < height) {
            model.getModelMatrix().setWidth(2.0);
            projection.scale(1.0, width / height);
          } else {
            projection.scale(height / width, 1.0);
          }
          if (this._viewMatrix != null) {
            projection.multiplyByMatrix(this._viewMatrix);
          }
          model.draw(projection);
        }
        ite2.preIncrement();
      }
    }

    const frameEnd = performance.now();
    this.lastFrameProfile = {
      totalMs: frameEnd - frameStart,
      updateMs: updateEnd - updateStart,
      drawMs: frameEnd - updateEnd,
      modelCount: updatedModels.size,
      passCount: this._renderPasses.length,
      drawableCount: totalDrawables,
    };
  }

  /**
   * 次のシーンに切りかえる
   * サンプルアプリケーションではモデルセットの切り替えを行う。
   */
  public nextScene(): void {
    const no: number = (this._sceneIndex + 1) % LAppDefine.ModelDirSize;
    this.changeScene(no);
  }

  /**
   * シーンを切り替える
   * サンプルアプリケーションではモデルセットの切り替えを行う。
   */
  public changeScene(index: number): void {
    this._sceneIndex = index;
    if (LAppDefine.DebugLogEnable) {
      LAppPal.printMessage(`[APP]model index: ${this._sceneIndex}`);
    }

    const modelId: string = LAppDefine.ModelDir[index];
    const modelPath: string = LAppDefine.ResourcesPath + modelId + '/';

    this.releaseAllModel();
    this._currentModelId = modelId;

    // Use the same pack.json-aware loading as changeModel
    this._loadModelOrPack(modelId, modelPath);
  }

  /**
   * モデルをIDで切り替える (Flutter から呼び出し可能)
   * メモリ節約のため、古いモデルを完全に解放してから新しいモデルをロード
   * @param modelId モデルのディレクトリ名 (例: 'model032', 'shuangsheng')
   * @returns 成功時はtrue、失敗時はfalse
   */
  public changeModel(modelId: string): boolean {
    // Hide canvas IMMEDIATELY via CSS — bulletproof anti-nude gate.
    // Only Dart can reveal via window.revealLive2DCanvas() after wardrobe is confirmed.
    const canvasEl = document.querySelector('canvas');
    if (canvasEl) (canvasEl as HTMLElement).style.opacity = '0';

    console.log(`[changeModel] Attempting to change to model: ${modelId}`);

    // モデルIDがModelDir配列に存在するか確認
    const index = LAppDefine.ModelDir.indexOf(modelId);

    if (index === -1) {
      console.error(`[changeModel] Model ID '${modelId}' not found in ModelDir`);
      console.error(`[changeModel] Available models: ${LAppDefine.ModelDir.join(', ')}`);
      return false;
    }

    if (typeof window.pauseLive2DRendering === 'function') {
      window.pauseLive2DRendering();
    }
    console.log(`[changeModel] Found model '${modelId}' at index ${index}`);

    // 現在表示中のモデルを完全に解放 (GPU/CPUメモリを解放)
    console.log(`[changeModel] Releasing old model to free memory...`);
    this.releaseAllModel();

    const modelPath: string = LAppDefine.ResourcesPath + modelId + '/';
    this._currentModelId = modelId;
    this._sceneIndex = index;

    // Try to load pack.json for multi-model support
    this._loadModelOrPack(modelId, modelPath);

    console.log(`[changeModel] Model '${modelId}' load initiated. Memory freed.`);
    return true;
  }

  /**
   * Attempt to load pack.json; if it exists load sub-models, otherwise
   * fall back to conventional single-model loading.
   */
  private _loadModelOrPack(modelId: string, modelPath: string): void {
    const packUrl = `${modelPath}pack.json`;

    fetch(packUrl)
      .then(response => {
        if (!response.ok) {
          // No pack.json — single-model fallback
          console.log(`[changeModel] No pack.json for '${modelId}', loading as single model`);
          this._loadSingleModel(modelId, modelPath);
          return;
        }
        return response.json();
      })
      .then(packData => {
        if (!packData) return; // Already handled in the !response.ok branch

        if (!packData.subModels || !Array.isArray(packData.subModels) || packData.subModels.length === 0) {
          console.warn(`[changeModel] pack.json for '${modelId}' has no valid subModels, loading as single model`);
          this._loadSingleModel(modelId, modelPath);
          return;
        }

        console.log(`[changeModel] Found pack.json for '${modelId}' with ${packData.subModels.length} sub-models`);
        this._loadPackModels(modelId, modelPath, packData.subModels as PackSubModel[]);
      })
      .catch(err => {
        // Network error or parse error — fall back to single model
        console.warn(`[changeModel] Error loading pack.json for '${modelId}', falling back to single model:`, err);
        this._loadSingleModel(modelId, modelPath);
      });
  }

  /**
   * Load a single model the conventional way (backward compatible).
   */
  private _loadSingleModel(modelId: string, modelPath: string): void {
    const modelJsonName: string = modelId + '.model3.json';

    console.log(`[changeModel] Loading single model from: ${modelPath}${modelJsonName}`);

    const model = new LAppModel();
    model.setModelYOffset(this._pendingModelYOffset, 0);
    model.setModelXOffset(this._pendingModelXOffset, 0);
    this._applyPendingLightToModel(model);
    model.loadAssets(modelPath, modelJsonName);

    this._models.setValue(modelId, model);
    this._currentSubModelKeys = [modelId];
    this._renderPasses = []; // No render passes for single model — uses legacy draw path

    // Apply pending wardrobe expressions before showing the model
    this.applyPendingWardrobeExpressions(model);

    console.log(`[changeModel] Single model '${modelId}' added to map and set as current.`);
  }

  /**
   * Load multiple sub-models from a pack.json definition.
   * Sub-models are sorted by drawOrder (lowest first = drawn behind).
   * Split models (with splitAt) generate two render passes.
   */
  private _loadPackModels(modelId: string, modelPath: string, subModels: PackSubModel[]): void {
    this._currentSubModelKeys = [];
    this._renderPasses = [];

    for (const sub of subModels) {
      const subPath = modelPath + sub.path;
      const subKey = `${modelId}:${sub.id}`;

      console.log(`[changeModel] Loading sub-model '${subKey}' from: ${subPath}${sub.model3}`);

      const model = new LAppModel();
      model.setModelYOffset(this._pendingModelYOffset, 0);
      model.setModelXOffset(this._pendingModelXOffset, 0);
      this._applyPendingLightToModel(model);
      if (sub.updateEveryNFrames && sub.updateEveryNFrames > 1) {
        model.updateEveryNFrames = sub.updateEveryNFrames;
        console.log(`[changeModel] Sub-model '${subKey}' throttled: update every ${sub.updateEveryNFrames} frames`);
      }
      if (sub.physicsEveryNFrames && sub.physicsEveryNFrames > 1) {
        model.physicsEveryNFrames = sub.physicsEveryNFrames;
        console.log(`[changeModel] Sub-model '${subKey}' physics throttled: every ${sub.physicsEveryNFrames} updates`);
      }
      if (sub.disablePhysics) {
        model.physicsEnabled = false;
        console.log(`[changeModel] Sub-model '${subKey}' physics disabled`);
      }
      model.loadAssets(subPath, sub.model3);

      this._models.setValue(subKey, model);
      this._currentSubModelKeys.push(subKey);

      // Build render passes
      if (sub.splitAt != null && sub.splitAt > 0) {
        // Split model → two passes
        this._renderPasses.push({
          subKey,
          drawOrder: sub.backDrawOrder ?? 0,
          minOrder: 0,
          maxOrder: sub.splitAt,
        });
        this._renderPasses.push({
          subKey,
          drawOrder: sub.frontDrawOrder ?? 2,
          minOrder: sub.splitAt,
          maxOrder: -1, // -1 = unbounded
        });
        console.log(`[changeModel] Sub-model '${subKey}' split at ${sub.splitAt}: back(order=${sub.backDrawOrder ?? 0}), front(order=${sub.frontDrawOrder ?? 2})`);
      } else {
        // Non-split model → single pass
        this._renderPasses.push({
          subKey,
          drawOrder: sub.drawOrder ?? 0,
          minOrder: -1,
          maxOrder: -1,
        });
      }
    }

    // Sort render passes by drawOrder ascending
    this._renderPasses.sort((a, b) => a.drawOrder - b.drawOrder);
    console.log(`[changeModel] Render passes (${this._renderPasses.length}):`, this._renderPasses.map(p => `${p.subKey}[${p.minOrder},${p.maxOrder}) @${p.drawOrder}`).join(', '));

    // Apply pending wardrobe expressions to all sub-models
    for (const key of this._currentSubModelKeys) {
      const model = this._models.getValue(key);
      if (model) {
        this.applyPendingWardrobeExpressions(model);
      }
    }

    console.log(`[changeModel] Pack '${modelId}' loaded with ${this._currentSubModelKeys.length} sub-models: ${this._currentSubModelKeys.join(', ')}`);
  }

  /**
   * Dynamically update the splitAt value for a given sub-model key.
   * Rebuilds the render passes for that sub-model in place.
   */
  public setSplitAt(subKey: string, splitAt: number): void {
    // Remove existing passes for this subKey
    const oldPasses = this._renderPasses.filter(p => p.subKey === subKey);
    if (oldPasses.length === 0) {
      console.warn(`[setSplitAt] No render passes found for '${subKey}'`);
      return;
    }

    // Preserve the draw orders from the original passes
    let backDrawOrder = 0;
    let frontDrawOrder = 2;
    if (oldPasses.length === 2) {
      // Already split — reuse existing draw orders
      backDrawOrder = oldPasses[0].drawOrder;
      frontDrawOrder = oldPasses[1].drawOrder;
    } else if (oldPasses.length === 1) {
      // Was not split before — use defaults
      backDrawOrder = 0;
      frontDrawOrder = 2;
    }

    // Remove old passes
    this._renderPasses = this._renderPasses.filter(p => p.subKey !== subKey);

    if (splitAt > 0) {
      // Split into two passes
      this._renderPasses.push({
        subKey,
        drawOrder: backDrawOrder,
        minOrder: 0,
        maxOrder: splitAt,
      });
      this._renderPasses.push({
        subKey,
        drawOrder: frontDrawOrder,
        minOrder: splitAt,
        maxOrder: -1,
      });
    } else {
      // No split — single pass
      this._renderPasses.push({
        subKey,
        drawOrder: backDrawOrder,
        minOrder: -1,
        maxOrder: -1,
      });
    }

    this._renderPasses.sort((a, b) => a.drawOrder - b.drawOrder);
    console.log(`[setSplitAt] Updated '${subKey}' splitAt=${splitAt}. Passes:`, this._renderPasses.map(p => `${p.subKey}[${p.minOrder},${p.maxOrder}) @${p.drawOrder}`).join(', '));
  }

  /**
   * Get the drawable count for a sub-model (useful for debug slider range).
   */
  public getSubModelDrawableCount(subKey: string): number {
    const model = this._models.getValue(subKey);
    if (!model || !model.getModel()) return 0;
    return model.getModel().getDrawableCount();
  }

  /**
   * Get current render passes (for debug display).
   */
  public getRenderPasses(): { subKey: string; drawOrder: number; minOrder: number; maxOrder: number }[] {
    return this._renderPasses;
  }


  /**
   * Reload the currently displayed model with updated resolution textures while keeping the
   * old textures visible until the replacement is ready. Intended for resolution tier changes
   * where the logical model remains the same.
   * @returns Promise that resolves to true when the reload completes (or forces success).
   */
  public async reloadCurrentModelWithResolution(): Promise<boolean> {
    if (this._resolutionReloadPromise) {
      console.warn('[reloadCurrentModelWithResolution] Reload already in progress, returning existing promise.');
      return this._resolutionReloadPromise;
    }

    const currentModelId = this._currentModelId;
    if (!currentModelId) {
      console.warn('[reloadCurrentModelWithResolution] No current model to reload');
      return false;
    }

    const index = LAppDefine.ModelDir.indexOf(currentModelId);
    if (index === -1) {
      console.error(`[reloadCurrentModelWithResolution] Current model "${currentModelId}" not found in ModelDir`);
      return false;
    }

    console.log(`[reloadCurrentModelWithResolution] Starting reload for model: ${currentModelId}`);

    // Pause rendering to freeze canvas on last frame (prevents default wardrobe flash)
    if (typeof window.pauseLive2DRendering === 'function') {
      window.pauseLive2DRendering();
    }

    const modelPath: string = LAppDefine.ResourcesPath + currentModelId + '/';
    const oldSubModelKeys = [...this._currentSubModelKeys];
    const timestamp = Date.now();

    // Build pairs of (oldKey, tempKey, newModel) for each sub-model.
    // For single-model packs, this is just one entry.
    interface ReloadEntry {
      oldKey: string;
      tempKey: string;
      newModel: LAppModel;
    }
    const entries: ReloadEntry[] = [];

    // Detect if this is a multi-model pack by checking if keys contain ':'
    const isPackModel = oldSubModelKeys.length > 0 && oldSubModelKeys[0].includes(':');

    if (isPackModel) {
      // Multi-model: reload each sub-model from its sub-path
      // We need to re-fetch pack.json or reconstruct paths from existing keys
      // Since keys are "modelId:subId", we can reconstruct the sub-path pattern
      // But we need the original pack.json data. Let's fetch it.
      try {
        const packResponse = await fetch(`${modelPath}pack.json`);
        if (!packResponse.ok) {
          console.error('[reloadCurrentModelWithResolution] Cannot find pack.json for multi-model reload');
          this._resolutionReloadPromise = null;
          return false;
        }
        const packData = await packResponse.json();
        const subModels: PackSubModel[] = packData.subModels || [];

        for (const sub of subModels) {
          const oldKey = `${currentModelId}:${sub.id}`;
          const tempKey = `${oldKey}_tierReload_${timestamp}`;
          const subPath = modelPath + sub.path;

          const newModel = new LAppModel();
          newModel.setModelYOffset(this._pendingModelYOffset, 0);
          newModel.setModelXOffset(this._pendingModelXOffset, 0);
          this._applyPendingLightToModel(newModel);
          newModel.loadAssets(subPath, sub.model3);

          this._models.setValue(tempKey, newModel);
          entries.push({ oldKey, tempKey, newModel });
        }
      } catch (err) {
        console.error('[reloadCurrentModelWithResolution] Error fetching pack.json:', err);
        this._resolutionReloadPromise = null;
        return false;
      }
    } else {
      // Single-model: reload the conventional way
      const modelJsonName: string = currentModelId + '.model3.json';
      const oldKey = currentModelId;
      const tempKey = `${currentModelId}_tierReload_${timestamp}`;

      const newModel = new LAppModel();
      newModel.setModelYOffset(this._pendingModelYOffset, 0);
      newModel.setModelXOffset(this._pendingModelXOffset, 0);
      this._applyPendingLightToModel(newModel);
      newModel.loadAssets(modelPath, modelJsonName);

      this._models.setValue(tempKey, newModel);
      entries.push({ oldKey, tempKey, newModel });
    }

    this._resolutionReloadPromise = new Promise<boolean>((resolve) => {
      let lastReportedState: LoadStep | null = null;
      let timeoutId: number | null = null;

      const finishSwap = (forced = false) => {
        console.log('[reloadCurrentModelWithResolution] Swapping to new model(s)');

        // Apply wardrobe to all new models
        for (const entry of entries) {
          this.applyPendingWardrobeExpressions(entry.newModel);
        }

        // Remove temp keys and release old models
        for (const entry of entries) {
          // Remove temp key
          let ite = this._models.begin();
          while (ite.notEqual(this._models.end())) {
            if (ite.ptr().first === entry.tempKey) {
              this._models.erase(ite);
              break;
            }
            ite.preIncrement();
          }

          // Release and remove old model
          ite = this._models.begin();
          while (ite.notEqual(this._models.end())) {
            if (ite.ptr().first === entry.oldKey) {
              const model = ite.ptr().second;
              if (model && model !== entry.newModel) {
                model.release();
                console.log(`[reloadCurrentModelWithResolution] Released old model resources for ${entry.oldKey}`);
              }
              this._models.erase(ite);
              break;
            }
            ite.preIncrement();
          }

          // Insert new model under original key
          this._models.setValue(entry.oldKey, entry.newModel);
        }

        this._sceneIndex = index;
        this._currentModelId = currentModelId;
        // Restore sub-model keys (same as before, in same draw order)
        this._currentSubModelKeys = entries.map(e => e.oldKey);

        window.clearInterval(checkInterval);
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (forced) {
          console.warn('[reloadCurrentModelWithResolution] Completed with timeout fallback; textures may still be streaming.');
        }

        this._resolutionReloadPromise = null;
        resolve(true);
      };

      const checkInterval = window.setInterval(() => {
        // Check if ALL new models are fully initialized
        let allReady = true;
        for (const entry of entries) {
          const loadState = entry.newModel.getLoadState();

          if (!entry.newModel.isFullyInitialized()) {
            if (loadState !== lastReportedState) {
              console.log(
                `[reloadCurrentModelWithResolution] Waiting for new textures... (state: ${loadState})`
              );
              lastReportedState = loadState;
            }
            allReady = false;
            break;
          }
        }

        if (!allReady) return;

        console.log('[reloadCurrentModelWithResolution] All new model(s) fully initialized with updated resolution');
        finishSwap();
      }, 100);

      timeoutId = window.setTimeout(() => {
        console.warn('[reloadCurrentModelWithResolution] Timeout while waiting for textures, forcing swap');
        finishSwap(true);
      }, 10000);
    });

    return this._resolutionReloadPromise;
  }

  /**
   * Wait for model data to be ready (expressions + motions loaded)
   * Similar to Dart's waitForModelDataReady method
   */
  private async waitForModelDataReady(model: LAppModel, timeoutMs: number = 10000): Promise<boolean> {
    const startTime = Date.now();
    const pollInterval = 200; // 200ms polling interval
    const loggedExpressionFailures = new Set<string>();
    let lastExpressionProgressKey = '';
    const pendingExpressionTracker = new Map<string, { firstDetected: number; warned: boolean }>();

    while (Date.now() - startTime < timeoutMs) {
      // Check if CubismModel is loaded
      const cubismModel = model.getModel();
      if (!cubismModel) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }

      // Check expression load status using summary data
      const expressionSummary = model.getExpressionLoadSummary();
      const expectedExpressions = expressionSummary.expectedNames.length;
      const loadedExpressions = expressionSummary.loadedNames.length;

      if (expressionSummary.failed.length > 0) {
        for (const failure of expressionSummary.failed) {
          if (!loggedExpressionFailures.has(failure.name)) {
            console.error(
              `[waitForModelDataReady] Expression "${failure.name}" failed to load from "${failure.filePath}". Reason: ${failure.reason}`
            );
            loggedExpressionFailures.add(failure.name);
          }
        }

        return false;
      }

      if (expectedExpressions > 0 && loadedExpressions < expectedExpressions) {
        const pendingNames = expressionSummary.pendingNames.slice().sort();
        const progressKey = `${loadedExpressions}/${expectedExpressions}:${pendingNames.join(',')}`;
        const now = Date.now();

        if (progressKey !== lastExpressionProgressKey) {
          if (pendingNames.length > 0) {
            console.log(
              `[waitForModelDataReady] Waiting for expressions: ${loadedExpressions}/${expectedExpressions} (pending: ${pendingNames.join(', ')})`
            );
          } else {
            console.log(
              `[waitForModelDataReady] Waiting for expressions: ${loadedExpressions}/${expectedExpressions}`
            );
          }
          lastExpressionProgressKey = progressKey;
        }

        // Record and warn about stalled expressions so developers can fix missing files quickly.
        const pendingSet = new Set(pendingNames);
        for (const detail of expressionSummary.pendingDetails) {
          const record = pendingExpressionTracker.get(detail.name);
          if (!pendingSet.has(detail.name)) {
            continue;
          }

          if (record) {
            if (!record.warned && now - record.firstDetected > 2000) {
              const elapsedSinceDetected = now - record.firstDetected;
              const fileInfo = detail.filePath ?? 'unknown path';
              console.warn(
                `[waitForModelDataReady] Expression "${detail.name}" still pending after ${elapsedSinceDetected}ms. Expected file: ${fileInfo}. This often indicates the asset is missing, misnamed, or not copied to the web demo.`
              );
              record.warned = true;
            }
          } else {
            pendingExpressionTracker.set(detail.name, {
              firstDetected: now,
              warned: false
            });
          }
        }

        // Clean up tracker entries for expressions that finished loading
        for (const trackedName of Array.from(pendingExpressionTracker.keys())) {
          if (!pendingSet.has(trackedName)) {
            pendingExpressionTracker.delete(trackedName);
          }
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));
        continue;
      }

      // Motions are deferred (loaded in background after CompleteSetup).
      // Do NOT block on them — only expressions + textures matter for display.

      // All data ready!
      const elapsed = Date.now() - startTime;
      console.log(`[waitForModelDataReady] Model data ready after ${elapsed}ms`);
      return true;
    }

    // Timeout
    const elapsed = Date.now() - startTime;
    console.warn(`[waitForModelDataReady] Timeout after ${elapsed}ms`);

    const finalSummary = model.getExpressionLoadSummary();
    if (finalSummary.pendingDetails.length > 0) {
      for (const detail of finalSummary.pendingDetails) {
        const fileInfo = detail.filePath ?? 'unknown path';
        console.warn(
          `[waitForModelDataReady] Timed out waiting for expression "${detail.name}". Expected file: ${fileInfo}. Verify the asset exists and the model JSON path is correct.`
        );
      }
    }
    return false;
  }

  private async waitForRequestedExpressionsReady(
    model: LAppModel,
    expressionNames: string[],
    timeoutMs: number = 12000
  ): Promise<boolean> {
    const requestedNames = Array.from(
      new Set(
        expressionNames
          .map(name => (typeof name === 'string' ? name.trim() : ''))
          .filter(name => name.length > 0)
      )
    );

    if (requestedNames.length === 0) {
      return true;
    }

    const startTime = Date.now();
    const pollInterval = 200;
    let lastProgressKey = '';
    const warnedPending = new Set<string>();

    while (Date.now() - startTime < timeoutMs) {
      const status = model.getRequestedExpressionStatus(requestedNames);

      if (status.failed.length > 0) {
        for (const failure of status.failed) {
          console.error(
            `[waitForRequestedExpressionsReady] Requested expression "${failure.name}" failed to load from "${failure.filePath ?? 'unknown path'}". Reason: ${failure.reason}`
          );
        }
        return false;
      }

      if (status.pendingNames.length === 0) {
        const elapsed = Date.now() - startTime;
        console.log(
          `[waitForRequestedExpressionsReady] Requested wardrobe expressions ready after ${elapsed}ms: ${status.loadedNames.join(', ')}`
        );
        return true;
      }

      const progressKey = `${status.loadedNames.length}/${status.requestedNames.length}:${status.pendingNames.join(',')}`;
      if (progressKey !== lastProgressKey) {
        console.log(
          `[waitForRequestedExpressionsReady] Waiting for requested wardrobe expressions: ${status.loadedNames.length}/${status.requestedNames.length} (pending: ${status.pendingNames.join(', ')})`
        );
        lastProgressKey = progressKey;
      }

      const elapsed = Date.now() - startTime;
      for (const detail of status.pendingDetails) {
        if (!warnedPending.has(detail.name) && elapsed > 2000) {
          console.warn(
            `[waitForRequestedExpressionsReady] Expression "${detail.name}" still pending after ${elapsed}ms. Expected file: ${detail.filePath ?? 'unknown path'}`
          );
          warnedPending.add(detail.name);
        }
      }

      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    const finalStatus = model.getRequestedExpressionStatus(requestedNames);
    if (finalStatus.pendingDetails.length > 0) {
      for (const detail of finalStatus.pendingDetails) {
        console.warn(
          `[waitForRequestedExpressionsReady] Timed out waiting for requested expression "${detail.name}". Expected file: ${detail.filePath ?? 'unknown path'}`
        );
      }
    }

    return finalStatus.pendingNames.length === 0 && finalStatus.failed.length === 0;
  }

  private async applyPendingWardrobeExpressions(model: LAppModel): Promise<number> {
    const wardrobeStartTime = performance.now();
    const pending = (window as any).__Live2DPendingWardrobeExpressions;

    if (!pending || typeof pending !== 'object') {
      // No wardrobe expressions to apply.
      // Do NOT clear the render block here — the Dart-side changeModel will
      // apply wardrobe textures shortly, which clears the block when done.
      // Only resume the render loop (not the draw block) if safe.
      if (!model.isTextureSwapActive && !model.isWardrobeRenderBlocked) {
        if (typeof window.resumeLive2DRendering === 'function') {
          window.resumeLive2DRendering();
        }
      }
      return 0;
    }

    const requestedExpressionNames = Array.from(
      new Set(
        Object.values(pending)
          .flatMap((rawValue) => {
            if (Array.isArray(rawValue)) {
              return rawValue
                .map((expr) => (typeof expr === 'string' ? expr.trim() : ''))
                .filter((expr) => expr.length > 0);
            }
            if (typeof rawValue === 'string') {
              return rawValue
                .split('+')
                .map((expr) => expr.trim())
                .filter((expr) => expr.length > 0);
            }
            return [] as string[];
          })
      )
    );

    // Wait for model data to be ready (expressions + motions loaded)
    const elapsed = Math.round(performance.now() - (window as any).__Live2DLoadStartTime || 0);
    console.log(`[applyPendingWardrobeExpressions] Waiting for model data to be ready... (+${elapsed}ms)`);
    const dataReady = await this.waitForModelDataReady(model, 10000);

    if (!dataReady) {
      const expressionSummary = model.getExpressionLoadSummary();

      if (expressionSummary.failed.length > 0) {
        for (const failure of expressionSummary.failed) {
          console.error(
            `[applyPendingWardrobeExpressions] Skipping wardrobe application because expression "${failure.name}" failed to load from "${failure.filePath}". Reason: ${failure.reason}`
          );
        }
      } else {
        console.warn('[applyPendingWardrobeExpressions] Model data not ready after timeout, skipping wardrobe application');
      }

      if (!model.isWardrobeRenderBlocked && typeof window.resumeLive2DRendering === 'function') {
        window.resumeLive2DRendering();
      }
      return 0;
    }

    const readyElapsed = Math.round(performance.now() - (window as any).__Live2DLoadStartTime || 0);
    console.log(`[applyPendingWardrobeExpressions] Model data ready, applying wardrobe... (+${readyElapsed}ms)`);

    const requestedReady = await this.waitForRequestedExpressionsReady(
      model,
      requestedExpressionNames,
      12000
    );

    if (!requestedReady) {
      console.warn(
        '[applyPendingWardrobeExpressions] Requested wardrobe expressions were not all ready before application; continuing with best-effort apply.'
      );
    }

    let appliedChannels = 0;

    for (const [channel, rawValue] of Object.entries(pending)) {
      let expressions: string[] = [];

      if (Array.isArray(rawValue)) {
        expressions = rawValue
          .map((expr) => (typeof expr === 'string' ? expr.trim() : ''))
          .filter((expr) => expr.length > 0);
      } else if (typeof rawValue === 'string') {
        expressions = rawValue
          .split('+')
          .map((expr) => expr.trim())
          .filter((expr) => expr.length > 0);
      }

      if (expressions.length === 0) {
        continue;
      }

      try {
        if (expressions.length === 1) {
          model.playExpressionInstant(channel, expressions[0]);
        } else {
          model.playMultipleExpressions(channel, expressions, true);
        }

        appliedChannels++;
      } catch (error) {
        console.error(
          `[reloadCurrentModelWithResolution] Failed to pre-apply expressions for channel "${channel}":`,
          error
        );
      }
    }

    if (appliedChannels > 0) {
      const totalElapsed = Math.round(performance.now() - (window as any).__Live2DLoadStartTime || 0);
      const wardrobeDuration = Math.round(performance.now() - wardrobeStartTime);
      console.log(
        `[reloadCurrentModelWithResolution] Applied pending wardrobe expressions to ${appliedChannels} channel${appliedChannels === 1 ? '' : 's'} in ${wardrobeDuration}ms (+${totalElapsed}ms total)`
      );
    }

    (window as any).__Live2DPendingWardrobeExpressions = null;

    // Only resume the render loop if no texture swap is active AND the
    // wardrobe render block is not set.  The draw block prevents nude
    // frames from being composited; it is cleared by _drainTextureSwapQueue
    // when the Dart-side wardrobe texture swap finishes.
    if (!model.isTextureSwapActive && !model.isWardrobeRenderBlocked) {
      if (typeof window.resumeLive2DRendering === 'function') {
        window.resumeLive2DRendering();
      }
    } else {
      console.log(
        '[applyPendingWardrobeExpressions] Wardrobe render blocked or texture swap in-flight — '
        + 'deferring rendering resume to texture swap queue drain'
      );
    }
    return appliedChannels;
  }

  private _applyPendingLightToModel(model: LAppModel | null): void {
    if (!model) {
      return;
    }

    const state = this._pendingLightState ?? this._defaultLightState();
    if (!state) {
      return;
    }

    const duration = state.transitionMs ?? DEFAULT_LIGHT_TRANSITION_MS;

    if (state.intensity <= 0) {
      model.resetLightColor(duration);
      return;
    }

    model.setLightColor(state.color, state.intensity, duration);
  }

  private _defaultLightState(): LightState {
    return {
      color: { r: 1, g: 1, b: 1, a: 1 },
      intensity: 0,
      transitionMs: DEFAULT_LIGHT_TRANSITION_MS
    };
  }

  private _cloneLightState(state: LightState): LightState {
    return {
      color: { ...state.color },
      intensity: state.intensity,
      transitionMs: state.transitionMs
    };
  }

  private _normalizeLightIntensity(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }
    if (value < 0) {
      return 0;
    }
    if (value > 1) {
      return 1;
    }
    return value;
  }

  private _normalizeColorChannel(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }
    if (value > 1) {
      const clamped = Math.max(0, Math.min(255, value));
      return clamped / 255;
    }
    if (value < 0) {
      return 0;
    }
    return value;
  }

  private _parseColorInput(
    colorInput: string | number | { r: number; g: number; b: number; a?: number }
  ): LightState['color'] | null {
    if (typeof colorInput === 'string') {
      const trimmed = colorInput.trim();
      if (trimmed.length === 0) {
        return null;
      }

      if (trimmed.startsWith('#')) {
        return this._parseHexColor(trimmed);
      }

      const rgbMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
      if (rgbMatch) {
        const parts = rgbMatch[1]
          .split(',')
          .map((part) => part.trim())
          .filter((part) => part.length > 0);

        if (parts.length < 3) {
          return null;
        }

        const r = parseFloat(parts[0]);
        const g = parseFloat(parts[1]);
        const b = parseFloat(parts[2]);
        const a = parts.length >= 4 ? parseFloat(parts[3]) : 1;

        return {
          r: this._normalizeColorChannel(r),
          g: this._normalizeColorChannel(g),
          b: this._normalizeColorChannel(b),
          a: this._normalizeColorChannel(a)
        };
      }

      return null;
    }

    if (typeof colorInput === 'number' && Number.isFinite(colorInput)) {
      const value = Math.floor(colorInput) >>> 0;
      const r = (value >>> 16) & 0xff;
      const g = (value >>> 8) & 0xff;
      const b = value & 0xff;
      const a = value > 0xffffff ? (value >>> 24) & 0xff : 255;

      return {
        r: this._normalizeColorChannel(r),
        g: this._normalizeColorChannel(g),
        b: this._normalizeColorChannel(b),
        a: this._normalizeColorChannel(a)
      };
    }

    if (typeof colorInput === 'object' && colorInput !== null) {
      const candidate = colorInput as {
        r?: number;
        g?: number;
        b?: number;
        a?: number;
      };

      if (
        candidate.r === undefined ||
        candidate.g === undefined ||
        candidate.b === undefined
      ) {
        return null;
      }

      return {
        r: this._normalizeColorChannel(candidate.r),
        g: this._normalizeColorChannel(candidate.g),
        b: this._normalizeColorChannel(candidate.b),
        a: this._normalizeColorChannel(
          candidate.a !== undefined ? candidate.a : 1
        )
      };
    }

    return null;
  }

  private _parseHexColor(hex: string): LightState['color'] | null {
    const normalized = hex.replace('#', '').trim();

    if (normalized.length === 3 || normalized.length === 4) {
      const r = normalized.charAt(0);
      const g = normalized.charAt(1);
      const b = normalized.charAt(2);
      const a = normalized.length === 4 ? normalized.charAt(3) : 'f';

      return {
        r: this._normalizeColorChannel(parseInt(r + r, 16)),
        g: this._normalizeColorChannel(parseInt(g + g, 16)),
        b: this._normalizeColorChannel(parseInt(b + b, 16)),
        a: this._normalizeColorChannel(parseInt(a + a, 16))
      };
    }

    if (normalized.length === 6 || normalized.length === 8) {
      const r = normalized.substring(0, 2);
      const g = normalized.substring(2, 4);
      const b = normalized.substring(4, 6);
      const a = normalized.length === 8 ? normalized.substring(6, 8) : 'ff';

      return {
        r: this._normalizeColorChannel(parseInt(r, 16)),
        g: this._normalizeColorChannel(parseInt(g, 16)),
        b: this._normalizeColorChannel(parseInt(b, 16)),
        a: this._normalizeColorChannel(parseInt(a, 16))
      };
    }

    return null;
  }

  public setViewMatrix(m: CubismMatrix44) {
    for (let i = 0; i < 16; i++) {
      this._viewMatrix.getArray()[i] = m.getArray()[i];
    }
  }

  /**
   * 現在表示中のモデル数を取得
   * @returns 表示中のモデル数
   */
  public getModelCount(): number {
    return this._models.getSize();
  }

  /**
   * コンストラクタ
   */
  constructor() {
    this._viewMatrix = new CubismMatrix44();
    this._models = new csmMap<string, LAppModel>();
    this._currentModelId = null;
    this._currentSubModelKeys = [];
    this._renderPasses = [];
    this._sceneIndex = 0;
    this._pendingModelYOffset = LAppDefine.ModelInitialYOffset;
    this._pendingModelYOffsetDuration = 0;
    this._pendingModelXOffset = 0;
    this._pendingModelXOffsetDuration = 0;
    this._pendingLightState = this._defaultLightState();
    // Don't load any model by default - wait for Flutter to call changeModel()
    console.log('[LAppLive2DManager] Initialized without loading default model');
  }

  _viewMatrix: CubismMatrix44; // モデル描画に用いるview行列
  _models: csmMap<string, LAppModel>; // モデルインスタンスのコンテナ (ModelID -> Model)
  _currentModelId: string; // 現在表示中のモデルID (pack-level)
  _currentSubModelKeys: string[]; // Keys in _models for active sub-models (ordered by drawOrder)
  _renderPasses: { subKey: string; drawOrder: number; minOrder: number; maxOrder: number }[]; // Multi-pass render order
  _sceneIndex: number; // 表示するシーンのインデックス値
  private _resolutionReloadPromise: Promise<boolean> | null = null;
  private _pendingModelYOffset: number;
  private _pendingModelYOffsetDuration: number;
  private _pendingModelXOffset: number;
  private _pendingModelXOffsetDuration: number;
  private _pendingLightState: LightState;
  // モーション再生終了のコールバック関数
  _finishedMotion = (self: ACubismMotion): void => {
    LAppPal.printMessage('Motion Finished:');
    console.log(self);
  };
}
